import React, { useEffect, useMemo } from 'react';

import { Controller, UseFormReturn } from 'react-hook-form';

import { useVersionStore } from 'store/version.store';

import { useQueryGetCardResourceTypes } from 'api/queries/getCardResourceType.query';

import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { Select } from 'components/DesignSystem/Select';

type Option = {
    id: number;
    value: number;
    label: string;
};

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    value?: number[];
    className?: string;
    isSelected?: boolean;
    versionId?: number;
}

export type ResourceType = 'MATERIAL' | 'WORK' | 'COST';

export const ResourceTypeFilter = ({
    methods,
    name,
    filtersStatusRef,
    handleFilters,
    className,
    value,
    isSelected = true,
    versionId,
}: Props) => {
    const { data, isSuccess } = useQueryGetCardResourceTypes({
        metaDataId: versionId,
    });

    const options: Option[] = useMemo(() => {
        return (
            data?.map((option) => {
                return {
                    id: option?.name,
                    value: option.name,
                    label: option.label,
                };
            }) ?? []
        );
    }, [data]);

    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
            return;
        }

        signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
    }, [value, name, filtersStatusRef, isSuccess]);

    const handleValue = (formValue: number[]): Option[] => {
        const result: Option[] = [];
        if (!formValue?.length) return result;

        options.forEach((listOptionElement: Option) => {
            const currentFormValueElement = formValue.find(
                (formValueElement) => formValueElement === listOptionElement.value,
            );

            if (currentFormValueElement) {
                result.push(listOptionElement);
            }
        });

        return result;
    };

    const handleChange = (selected: readonly ISelectOption<string>[]) => {
        methods?.setValue(
            name,
            selected.map((option) => option.value),
        );
        handleFilters && handleFilters();
    };

    return isSelected ? (
        <div className={`${classes.selectWrapper} ${className ?? ''}`}>
            <div data-testid={'battlecards-filters-resource-type'} className={classes.activityCodesSelectWrapper}>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<number>>
                            id={'global-filter-component-resource-type-filter'}
                            {...field}
                            options={options}
                            isMulti
                            value={handleValue(value)}
                            onChange={handleChange}
                            hideSelectedOptions={true}
                            label={FilterLabels.resourceType}
                        />
                    )}
                    name={name}
                    control={methods?.control}
                />
            </div>
        </div>
    ) : null;
};
